import { clear, el, setClass } from './dom';
import { icon } from './Icons';
import { compareSlider } from './EnhancePanel';
import type { ManifestSite } from '@/capture/SceneManifest';
import type { PhotoStorage, StoredShot } from '@/capture/PhotoStorage';

/**
 * **Shots** — the saved frames as a place you go, rather than a drawer inside the camera.
 *
 * ## Why this is not the gallery
 *
 * `PhotoGallery` is a strip down the left of photo mode, and that is the right shape for what it is
 * for: you are composing, you want to see what you already have, you slide it out and push it back.
 * It is the wrong shape for the other errand entirely — *going through the shots*. A 260 px column
 * of thumbnails, scrolled, inside a mode that has hidden the editor, is a filing cabinet you have to
 * stand inside to open.
 *
 * So the toolbar's Shots button lands here instead: a full sheet over the designer, with the site
 * still behind it. Two things follow from being outside photo mode, and both are the point:
 *
 * - **A pair can be opened full screen.** A capture and the render made from it are one thing seen
 *   twice, and the only honest way to look at them is the same pixels at the same size with one
 *   divider between — see {@link openViewer}. In a 260 px drawer that comparison is decorative.
 * - **A shot can be re-staged.** Restoring the camera and the date puts you back where the shutter
 *   fired, which is only useful if there is a designer to be put back *into*. Photo mode's recall
 *   does the same thing and then leaves you in the viewfinder; this returns you to the site.
 *
 * ## Re-staging is offered only where it means something
 *
 * A shot carries the id of the document it was taken of ({@link ManifestSite.id}). Re-stage is
 * offered when that id matches the site currently open, and is otherwise a disabled button carrying
 * the reason — not a hidden one. The distinction the button draws is real: pushing a camera from one
 * garden into another garden restores a viewpoint onto ground that was never there, which looks like
 * a bug and is worse than a refusal. See {@link restageVerdict} for every case and what it says.
 *
 * Shots taken before this became true have no site id at all, and are honestly labelled as such
 * rather than being quietly matched by name or by site dimensions.
 *
 * ## Nothing here owns anything
 *
 * The store is resolved per call, because storage changes hands at sign-in and a captured reference
 * would go on writing to the device after the account took over. Images come from `ShotImage.url`,
 * whose lifetime belongs to the store — this panel mints no object URLs and revokes none.
 */

/* ------------------------------------------------------------------------------------------- *
 * The model. Pure, and deliberately stated against a structural shape rather than `StoredShot`,
 * so every decision below can be exercised without a Blob, an IndexedDB, or a DOM.
 * ------------------------------------------------------------------------------------------- */

/** The document a shot might belong to. `id` is null for a draft that has never been saved. */
export interface ShotSiteRef {
  id: string | null;
  name: string;
}

/** What the panel needs to read off a shot. `StoredShot` satisfies it. */
export interface ShotFacts {
  title: string;
  createdAt: number;
  manifest: {
    site: ManifestSite | null;
    environment: { seasonLabel: string; timeLabel: string; dateLabel: string };
    visiblePlantCount: number;
    legend: readonly { botanicalName: string; commonName: string }[];
  };
  variants: readonly { id: string; presetLabel: string }[];
}

/** Which shots the sheet is showing. */
export type ShotScope = 'all' | 'site' | 'enhanced';

export interface ShotQuery {
  search: string;
  scope: ShotScope;
}

export function defaultShotQuery(): ShotQuery {
  return { search: '', scope: 'all' };
}

export const SHOT_SCOPES: readonly { id: ShotScope; label: string; hint: string }[] = [
  { id: 'all', label: 'All', hint: 'Every shot in this library' },
  { id: 'site', label: 'This site', hint: 'Only shots taken of the site that is open' },
  { id: 'enhanced', label: 'With renders', hint: 'Only shots that have an AI render attached' },
];

/** The document id a shot was taken of, or null when it does not carry one. */
export function shotSiteId(shot: ShotFacts): string | null {
  return shot.manifest.site?.id ?? null;
}

/** The document name recorded at capture time, or null. Renames do not chase it. */
export function shotSiteName(shot: ShotFacts): string | null {
  return shot.manifest.site?.name ?? null;
}

export interface RestageVerdict {
  allowed: boolean;
  /** Shown as the button's tooltip either way — the reason it is offered, or the reason it is not. */
  reason: string;
}

/**
 * Whether this shot's frame can be put back onto the site that is open.
 *
 * Every "no" states which of the four ways it can fail applies, because they are not the same
 * problem and only one of them is the user's to fix. A shot from another garden is a filing
 * question; a shot with no site id is a shot older than the field; an unsaved draft resolves itself
 * the moment the site is saved.
 */
export function restageVerdict(shot: ShotFacts, current: ShotSiteRef): RestageVerdict {
  const site = shot.manifest.site;
  if (site === null) {
    return { allowed: false, reason: 'This shot records no site, so there is no frame to put back.' };
  }
  const id = site.id ?? null;
  if (id === null) {
    return {
      allowed: false,
      reason: 'Taken before this site had been saved, so it cannot be matched to one.',
    };
  }
  if (current.id === null) {
    return {
      allowed: false,
      reason: 'The open site has never been saved, so nothing can be matched against it.',
    };
  }
  if (id !== current.id) {
    const name = site.name ?? 'another site';
    return { allowed: false, reason: `Belongs to ${name} — open that site to re-stage this shot.` };
  }
  return {
    allowed: true,
    reason: 'Put the camera and the date back where this shot was taken.',
  };
}

/** How a shot's site reads on its card: the label, and the tone that goes with it. */
export function describeShotSite(
  shot: ShotFacts,
  current: ShotSiteRef,
): { label: string; tone: 'this' | 'other' | 'unknown' } {
  const site = shot.manifest.site;
  if (site === null) return { label: 'No site recorded', tone: 'unknown' };
  if (site.id === undefined) return { label: 'Site not yet saved', tone: 'unknown' };
  if (site.id === current.id) return { label: 'This site', tone: 'this' };
  return { label: site.name ?? 'Another site', tone: 'other' };
}

/** The one-line caption under a shot: when in the year it is, and how much is standing in it. */
export function describeShot(shot: ShotFacts): string {
  const { environment } = shot.manifest;
  const plants = shot.manifest.visiblePlantCount;
  return (
    `${environment.dateLabel} · ${environment.seasonLabel} · ${environment.timeLabel} · ` +
    `${plants} plant${plants === 1 ? '' : 's'}`
  );
}

/**
 * One thing the viewer can show: the capture on its own, or the capture against one render.
 *
 * The capture is always first and always present — a shot with no renders is still worth seeing at
 * full size, and making the viewer refuse to open one would mean the button on the card lied.
 */
export interface ShotFrame {
  /** Empty for the capture itself, otherwise the variant's id. */
  variantId: string;
  label: string;
}

export function shotFrames(shot: ShotFacts): ShotFrame[] {
  return [
    { variantId: '', label: 'Capture' },
    ...shot.variants.map((variant) => ({ variantId: variant.id, label: variant.presetLabel })),
  ];
}

/** Whether a shot answers the current query. Search runs over the title and the species in frame. */
export function matchesShotQuery(shot: ShotFacts, query: ShotQuery, current: ShotSiteRef): boolean {
  if (query.scope === 'enhanced' && shot.variants.length === 0) return false;
  if (query.scope === 'site' && (shotSiteId(shot) === null || shotSiteId(shot) !== current.id)) {
    return false;
  }

  const needle = query.search.trim().toLowerCase();
  if (needle === '') return true;

  const haystack = [
    shot.title,
    shotSiteName(shot) ?? '',
    shot.manifest.environment.seasonLabel,
    shot.manifest.environment.dateLabel,
    ...shot.manifest.legend.flatMap((entry) => [entry.botanicalName, entry.commonName]),
    ...shot.variants.map((variant) => variant.presetLabel),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export function filterShots<T extends ShotFacts>(
  shots: readonly T[],
  query: ShotQuery,
  current: ShotSiteRef,
): T[] {
  return shots.filter((shot) => matchesShotQuery(shot, query, current));
}

/** A filename safe on every platform, with the render's preset appended when there is one. */
export function shotFileName(title: string, suffix?: string): string {
  const base = title.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '') || 'shot';
  const tail = suffix ? `-${suffix.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '')}` : '';
  return `${base}${tail}.png`;
}

/** The footer line: what is on screen, out of what, and whether it will survive a reload. */
export function shotsSummary(
  shown: number,
  total: number,
  renders: number,
  persistent: boolean,
): string {
  if (total === 0) return 'No shots yet.';
  const scope = shown === total ? `${total} shot${total === 1 ? '' : 's'}` : `${shown} of ${total} shots`;
  const enhanced = renders > 0 ? ` · ${renders} render${renders === 1 ? '' : 's'}` : '';
  const warning = persistent ? '' : ' · not saved to disk — this session only';
  return `${scope}${enhanced}${warning}`;
}

/* ------------------------------------------------------------------------------------------- *
 * The sheet.
 * ------------------------------------------------------------------------------------------- */

export interface ShotsPanelDeps {
  /**
   * Where the shots live. Resolved per call rather than captured: storage changes hands at sign-in,
   * and a held reference would keep reading the device after the account took over.
   */
  store: () => PhotoStorage | null;
  /** The document currently open, so a shot taken of it can be recognised. */
  currentSite: () => ShotSiteRef;
  /** Put a shot's camera and environment back onto the live site. */
  onRestage: (shot: StoredShot) => void;
  /** The sheet opened or closed, so the toolbar button can show itself pressed. */
  onOpenChange?: (open: boolean) => void;
}

export class ShotsPanel {
  readonly root: HTMLElement;

  private shots: StoredShot[] = [];
  private query: ShotQuery = defaultShotQuery();
  private loading = false;
  private failure: string | null = null;
  /** The shot the full-screen viewer is on, and which of its frames. Null when the viewer is shut. */
  private viewing: { shotId: string; variantId: string } | null = null;
  /** Delete asks twice. Holds the id waiting for its second press. */
  private confirmingId: string | null = null;

  private readonly scrim: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly siteChip: HTMLElement;
  private readonly scopeBar: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly gridEl: HTMLElement;
  private readonly summaryEl: HTMLElement;
  private readonly viewer: HTMLElement;
  private readonly viewerBar: HTMLElement;
  private readonly viewerStage: HTMLElement;
  private readonly viewerFoot: HTMLElement;

  /**
   * Escape unwinds one layer at a time: the full-screen pair, then a query, then the sheet. Bound in
   * the capture phase so it beats the editor's own key handling while the sheet is up.
   */
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      if (this.viewing !== null) {
        this.closeViewer();
        return;
      }
      if (this.query.search !== '' || this.query.scope !== 'all') {
        this.searchInput.value = '';
        this.setQuery(defaultShotQuery());
        return;
      }
      this.close();
      return;
    }

    // Arrow keys step between shots in the viewer — except while the compare slider itself has
    // focus, where they are the slider's own controls and stepping away would be a trap.
    if (this.viewing === null) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') return;
    event.stopPropagation();
    event.preventDefault();
    this.stepViewer(event.key === 'ArrowRight' ? 1 : -1);
  };

  constructor(private readonly deps: ShotsPanelDeps) {
    this.countEl = el('span', { class: 'lg-shots__count' });
    this.siteChip = el('span', { class: 'lg-shots__site' });

    this.scopeBar = el('div', {
      class: 'lg-segment lg-segment--tight lg-shots__scopes',
      attrs: { role: 'group', 'aria-label': 'Which shots to show' },
    });
    for (const scope of SHOT_SCOPES) {
      this.scopeBar.appendChild(
        el('button', {
          class: 'lg-segment__item',
          text: scope.label,
          title: scope.hint,
          attrs: { type: 'button', 'data-scope': scope.id },
          on: { click: () => this.setQuery({ scope: scope.id }) },
        }),
      );
    }

    this.searchInput = el('input', {
      class: 'lg-shots__search',
      attrs: {
        type: 'search',
        placeholder: 'Search shots — name, species, season…',
        'aria-label': 'Search the saved shots',
        spellcheck: 'false',
      },
      on: { input: () => this.setQuery({ search: this.searchInput.value }) },
    });

    this.gridEl = el('div', { class: 'lg-shots__grid' });
    this.summaryEl = el('span', { class: 'lg-shots__summary' });

    this.sheet = el(
      'div',
      { class: 'lg-shots__sheet', attrs: { role: 'dialog', 'aria-label': 'Saved shots' } },
      el(
        'header',
        { class: 'lg-shots__head' },
        el('span', { class: 'lg-shots__headicon', html: icon('gallery', 17) }),
        el(
          'div',
          { class: 'lg-shots__heading' },
          el('h2', { class: 'lg-shots__title', text: 'Shots' }),
          el('span', {
            class: 'lg-shots__subtitle',
            text: 'Every frame saved from this library',
          }),
        ),
        this.countEl,
        this.siteChip,
        el('div', { class: 'lg-shots__spacer' }),
        el('button', {
          class: 'lg-shots__iconbutton',
          html: icon('close', 16),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the shots browser' },
          on: { click: () => this.close() },
        }),
      ),
      el(
        'div',
        { class: 'lg-shots__bar' },
        this.scopeBar,
        el(
          'div',
          { class: 'lg-shots__searchwrap' },
          el('span', { class: 'lg-shots__searchicon', html: icon('search', 14) }),
          this.searchInput,
        ),
      ),
      el('div', { class: 'lg-shots__main' }, this.gridEl),
      el('footer', { class: 'lg-shots__foot' }, this.summaryEl),
    );

    this.viewerBar = el('header', { class: 'lg-shotview__bar' });
    this.viewerStage = el('div', { class: 'lg-shotview__stage' });
    this.viewerFoot = el('footer', { class: 'lg-shotview__foot' });
    this.viewer = el(
      'div',
      {
        class: 'lg-shotview',
        attrs: { hidden: true, role: 'dialog', 'aria-label': 'Shot and render, full screen' },
      },
      this.viewerBar,
      this.viewerStage,
      this.viewerFoot,
    );

    this.scrim = el('div', { class: 'lg-shots__scrim', on: { click: () => this.close() } });

    this.root = el(
      'div',
      { class: 'lg-shots', attrs: { hidden: true } },
      this.scrim,
      this.sheet,
      this.viewer,
    );

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeyDown, true);
    }
  }

  /* --------------------------------- Open and close --------------------------------- */

  get isOpen(): boolean {
    return this.root.hidden === false;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.root.hidden = false;
    this.confirmingId = null;
    this.render();
    void this.reload();
    this.searchInput.focus();
    this.deps.onOpenChange?.(true);
  }

  close(): void {
    if (!this.isOpen) return;
    this.closeViewer();
    this.root.hidden = true;
    this.confirmingId = null;
    this.deps.onOpenChange?.(false);
  }

  /** Re-read the store. Safe to call while shut — it simply redraws nothing. */
  async refresh(): Promise<void> {
    if (!this.isOpen) return;
    await this.reload();
  }

  private async reload(): Promise<void> {
    const store = this.deps.store();
    if (!store) {
      this.shots = [];
      this.failure = 'Shot storage is not available in this session.';
      this.render();
      return;
    }

    this.loading = true;
    this.failure = null;
    this.render();
    try {
      this.shots = await store.list();
    } catch (error) {
      this.shots = [];
      this.failure = error instanceof Error ? error.message : 'The shot library could not be read.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private setQuery(patch: Partial<ShotQuery>): void {
    this.query = { ...this.query, ...patch };
    this.confirmingId = null;
    this.render();
    this.gridEl.scrollTop = 0;
  }

  /* ------------------------------------ The sheet ----------------------------------- */

  private get site(): ShotSiteRef {
    return this.deps.currentSite();
  }

  private render(): void {
    if (!this.isOpen) return;
    const current = this.site;
    const shown = filterShots(this.shots, this.query, current);

    for (const button of this.scopeBar.querySelectorAll('button')) {
      setClass(button, 'is-active', button.dataset.scope === this.query.scope);
    }
    // Hidden rather than emptied: the chip has a pill background, so an empty one is a grey dash
    // floating beside the title with nothing to say.
    this.countEl.hidden = this.shots.length === 0;
    this.countEl.textContent = `${shown.length} / ${this.shots.length}`;

    clear(this.siteChip);
    this.siteChip.className = `lg-shots__site${current.id === null ? ' is-unsaved' : ''}`;
    this.siteChip.title =
      current.id === null
        ? 'This site has never been saved, so no shot can be matched to it yet'
        : 'Shots taken of this site can be re-staged in the designer';
    this.siteChip.append(
      el('span', { class: 'lg-shots__sitedot' }),
      el('span', { text: current.name }),
    );

    const renders = this.shots.reduce((total, shot) => total + shot.variants.length, 0);
    const store = this.deps.store();
    this.summaryEl.textContent = shotsSummary(
      shown.length,
      this.shots.length,
      renders,
      store?.persistent ?? true,
    );

    this.renderGrid(shown, current);
    if (this.viewing !== null) this.renderViewer();
  }

  private renderGrid(shown: readonly StoredShot[], current: ShotSiteRef): void {
    const scroll = this.gridEl.scrollTop;
    clear(this.gridEl);

    if (this.failure !== null) {
      this.gridEl.appendChild(this.notice('warning', this.failure));
      return;
    }
    if (this.loading && this.shots.length === 0) {
      this.gridEl.appendChild(this.notice('gallery', 'Reading the shot library…'));
      return;
    }
    if (this.shots.length === 0) {
      this.gridEl.appendChild(
        this.notice(
          'camera',
          'No shots yet. Press C for photo mode, frame the garden, and hit the shutter — ' +
            'every shot lands here with the camera and the date it was taken under.',
        ),
      );
      return;
    }
    if (shown.length === 0) {
      this.gridEl.appendChild(
        this.notice('search', 'No shot in the library matches this query.'),
      );
      return;
    }

    for (const shot of shown) this.gridEl.appendChild(this.card(shot, current));
    this.gridEl.scrollTop = scroll;
  }

  private notice(iconName: Parameters<typeof icon>[0], message: string): HTMLElement {
    return el(
      'div',
      { class: 'lg-shots__empty' },
      el('span', { class: 'lg-shots__emptyicon', html: icon(iconName, 24) }),
      el('p', { text: message }),
    );
  }

  private card(shot: StoredShot, current: ShotSiteRef): HTMLElement {
    const verdict = restageVerdict(shot, current);
    const site = describeShotSite(shot, current);
    const renders = shot.variants.length;
    const confirming = this.confirmingId === shot.id;

    return el(
      'article',
      { class: 'lg-shots__card', attrs: { 'data-shot': shot.id } },
      el(
        'button',
        {
          class: 'lg-shots__thumb',
          attrs: {
            type: 'button',
            'aria-label': `Open ${shot.title} full screen`,
          },
          title:
            renders > 0
              ? `Open ${shot.title} full screen — the capture and its ${renders} render${renders === 1 ? '' : 's'}, side by side`
              : `Open ${shot.title} full screen`,
          on: { click: () => this.openViewer(shot.id) },
        },
        el('img', { attrs: { src: shot.thumbnail.url, alt: shot.title, loading: 'lazy' } }),
        renders > 0
          ? el(
              'span',
              { class: 'lg-shots__badge' },
              el('span', { class: 'lg-shots__badgeicon', html: icon('sparkle', 11) }),
              el('span', { text: String(renders) }),
            )
          : null,
        el(
          'span',
          { class: 'lg-shots__reveal' },
          el('span', { html: icon('expand', 15) }),
          el('span', { text: renders > 0 ? 'Compare' : 'Full screen' }),
        ),
      ),
      el(
        'div',
        { class: 'lg-shots__body' },
        el('span', { class: 'lg-shots__name', text: shot.title }),
        el('span', { class: 'lg-shots__meta', text: describeShot(shot) }),
        el('span', {
          class: `lg-shots__origin is-${site.tone}`,
          text: site.label,
          title: verdict.reason,
        }),
      ),
      el(
        'div',
        { class: 'lg-shots__actions' },
        el(
          'button',
          {
            class: 'lg-shots__action lg-shots__action--primary',
            title: verdict.reason,
            attrs: {
              type: 'button',
              disabled: !verdict.allowed,
              'aria-label': `Re-stage ${shot.title} in the designer`,
            },
            on: { click: () => this.restage(shot) },
          },
          el('span', { class: 'lg-shots__actionicon', html: icon('focus', 13) }),
          el('span', { text: 'Re-stage' }),
        ),
        el(
          'a',
          {
            class: 'lg-shots__action',
            title: 'Download the original capture',
            attrs: {
              href: shot.beauty.url,
              download: shotFileName(shot.title),
              'aria-label': `Download ${shot.title}`,
            },
          },
          el('span', { class: 'lg-shots__actionicon', html: icon('download', 13) }),
          el('span', { text: 'Download' }),
        ),
        el(
          'button',
          {
            class: `lg-shots__action lg-shots__action--danger${confirming ? ' is-armed' : ''}`,
            title: confirming
              ? 'Press again to delete this shot and every render on it'
              : 'Delete this shot',
            attrs: { type: 'button', 'aria-label': `Delete ${shot.title}` },
            on: { click: () => this.requestDelete(shot) },
          },
          el('span', { class: 'lg-shots__actionicon', html: icon('delete', 13) }),
          el('span', { text: confirming ? 'Sure?' : 'Delete' }),
        ),
      ),
    );
  }

  private restage(shot: StoredShot): void {
    if (!restageVerdict(shot, this.site).allowed) return;
    this.deps.onRestage(shot);
    // Closing is the whole gesture: re-staging is a request to be put back in the garden, and a
    // sheet still covering it would have to be dismissed before the result could be seen.
    this.close();
  }

  /** Delete asks twice, in place. A contact sheet is exactly where a misfire is easiest. */
  private requestDelete(shot: StoredShot): void {
    if (this.confirmingId !== shot.id) {
      this.confirmingId = shot.id;
      this.render();
      return;
    }
    this.confirmingId = null;
    if (this.viewing?.shotId === shot.id) this.closeViewer();
    const store = this.deps.store();
    if (!store) return;
    void store.remove(shot.id).then(() => this.reload());
  }

  /* ---------------------------------- The full screen -------------------------------- */

  /**
   * Open one shot at the size of the window.
   *
   * Lands on the newest render when there is one, because that is what the shot was opened to look
   * at — a capture that has been enhanced is being examined *against* the enhancement. With no
   * renders it opens on the capture, which is still worth seeing whole.
   */
  private openViewer(shotId: string, variantId?: string): void {
    const shot = this.shots.find((entry) => entry.id === shotId);
    if (!shot) return;
    this.viewing = {
      shotId,
      variantId: variantId ?? shot.variants[0]?.id ?? '',
    };
    this.viewer.hidden = false;
    this.renderViewer();
  }

  private closeViewer(): void {
    this.viewing = null;
    this.viewer.hidden = true;
    clear(this.viewerStage);
  }

  /** Move to the neighbouring shot in the filtered list, wrapping at both ends. */
  private stepViewer(delta: number): void {
    const viewing = this.viewing;
    if (viewing === null) return;
    const shown = filterShots(this.shots, this.query, this.site);
    if (shown.length === 0) return;
    const index = shown.findIndex((shot) => shot.id === viewing.shotId);
    const next = shown[(((index + delta) % shown.length) + shown.length) % shown.length];
    if (next) this.openViewer(next.id);
  }

  private renderViewer(): void {
    const viewing = this.viewing;
    if (viewing === null) return;
    const shot = this.shots.find((entry) => entry.id === viewing.shotId);
    if (!shot) {
      this.closeViewer();
      return;
    }

    const frames = shotFrames(shot);
    const active = frames.find((frame) => frame.variantId === viewing.variantId) ?? frames[0];
    const variant = shot.variants.find((entry) => entry.id === active?.variantId);
    const verdict = restageVerdict(shot, this.site);
    const shown = filterShots(this.shots, this.query, this.site);
    const position = shown.findIndex((entry) => entry.id === shot.id);

    /* ---- the bar ---- */
    clear(this.viewerBar);
    this.viewerBar.append(
      el('button', {
        class: 'lg-shotview__iconbutton',
        html: icon('rewind', 15),
        title: 'Previous shot  ·  ←',
        attrs: { type: 'button', 'aria-label': 'Previous shot', disabled: shown.length < 2 },
        on: { click: () => this.stepViewer(-1) },
      }),
      el('button', {
        class: 'lg-shotview__iconbutton',
        html: icon('forward', 15),
        title: 'Next shot  ·  →',
        attrs: { type: 'button', 'aria-label': 'Next shot', disabled: shown.length < 2 },
        on: { click: () => this.stepViewer(1) },
      }),
      el(
        'div',
        { class: 'lg-shotview__heading' },
        el('span', { class: 'lg-shotview__title', text: shot.title }),
        el('span', { class: 'lg-shotview__meta', text: describeShot(shot) }),
      ),
    );

    // The frame strip. One chip per render plus the capture, so switching between two renders of the
    // same frame is one click and the capture is never more than one click away from either.
    if (frames.length > 1) {
      const strip = el('div', {
        class: 'lg-segment lg-segment--tight lg-shotview__frames',
        attrs: { role: 'group', 'aria-label': 'Which render to compare against' },
      });
      for (const frame of frames) {
        strip.appendChild(
          el('button', {
            class: `lg-segment__item${frame.variantId === active?.variantId ? ' is-active' : ''}`,
            text: frame.label,
            title:
              frame.variantId === ''
                ? 'The capture on its own, with nothing over it'
                : `Compare the capture against ${frame.label}`,
            attrs: { type: 'button' },
            on: { click: () => this.openViewer(shot.id, frame.variantId) },
          }),
        );
      }
      this.viewerBar.appendChild(strip);
    }

    this.viewerBar.append(
      el('div', { class: 'lg-shots__spacer' }),
      el(
        'button',
        {
          class: 'lg-shotview__button',
          title: verdict.reason,
          attrs: { type: 'button', disabled: !verdict.allowed },
          on: { click: () => this.restage(shot) },
        },
        el('span', { html: icon('focus', 13) }),
        el('span', { text: 'Re-stage' }),
      ),
      el(
        'a',
        {
          class: 'lg-shotview__button',
          title: variant ? 'Download this render' : 'Download the capture',
          attrs: {
            href: variant ? variant.image.url : shot.beauty.url,
            download: shotFileName(shot.title, variant?.presetId),
          },
        },
        el('span', { html: icon('download', 13) }),
        el('span', { text: 'Download' }),
      ),
      el('button', {
        class: 'lg-shotview__iconbutton',
        html: icon('close', 16),
        title: 'Back to the contact sheet  ·  Esc',
        attrs: { type: 'button', 'aria-label': 'Close the full-screen view' },
        on: { click: () => this.closeViewer() },
      }),
    );

    /* ---- the picture ---- */
    clear(this.viewerStage);
    // The stage sizes the picture to fill the window, and it can only do that without letterboxing
    // if it knows the shot's shape — the divider is positioned as a percentage of its box, so the
    // box has to be the image exactly. See the stage rules in `shots.css`.
    this.viewerStage.style.setProperty(
      '--shot-aspect',
      `${shot.size.width} / ${shot.size.height}`,
    );
    this.viewerStage.appendChild(
      variant
        ? compareSlider(shot.beauty.url, variant.image.url, {
            beforeLabel: 'Capture',
            afterLabel: variant.presetLabel,
          })
        : el(
            'div',
            { class: 'lg-shotview__solo' },
            el('img', { attrs: { src: shot.beauty.url, alt: shot.title } }),
          ),
    );

    /* ---- the caption ---- */
    clear(this.viewerFoot);
    this.viewerFoot.append(
      el('span', {
        class: 'lg-shotview__caption',
        text: variant
          ? `Drag the divider — the capture on the left, ${variant.presetLabel} on the right. ` +
            'Both sit in identical screen positions, so any drift the model introduced shows itself.'
          : shot.variants.length === 0
            ? 'This shot has no render on it yet. Enhance it from photo mode and it will appear here.'
            : 'The capture on its own. Pick a render above to compare against it.',
      }),
      el('div', { class: 'lg-shots__spacer' }),
      el('span', {
        class: 'lg-shotview__position',
        text: position >= 0 ? `${position + 1} of ${shown.length}` : '',
      }),
    );
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown, true);
    }
    this.root.remove();
  }
}
